import React, { useCallback } from 'react';
import {
  ActionIcon,
  Button,
  Divider,
  Group,
  NumberInput,
  Radio,
  Select,
  Stack,
  Switch,
  Text,
} from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { createUsePuck } from '@puckeditor/core';
import {
  GRID_COLUMNS,
  STACK_AT_SMALL_PX,
  STACK_AT_MEDIUM_PX,
  STACK_NEVER_PX,
  addSectionColumn,
  getColumnSpanValues,
  removeSectionColumn,
  resolveSectionColumns,
  resolveStackAtPx,
  setSectionColumnSpan,
  zoneKeyForSectionColumn,
} from '../../utils/proposalSectionColumns';
import {
  applyNestedMainColumnCount,
  getMainColumnSpanValues,
  getRowColumnSpanValues,
  rowToNestedLayout,
  setNestedMainColumnSpan,
  setNestedRowColumnCount,
  setNestedRowColumnSpan,
  setNestedRowCount,
} from '../../utils/proposalSectionNested';
import {
  SECTION_LAYOUT_MIRRORS,
  SECTION_LAYOUT_PRESETS,
  SECTION_PRESET_NESTED,
  SECTION_PRESET_ROW,
  buildSectionLayout,
  collectSectionZoneIds,
  getRemovedColumnIds,
  isSectionLayoutCustom,
  reorderIdsForMirror,
  sectionMatchesLayoutPreset,
} from '../../utils/proposalSectionPresets';
import SectionLayoutPresetPicker from './SectionLayoutPresetPicker';
import InspectorRow from './InspectorRow';
import { useAdvancedSectionOpen } from './puckSectionField';
import { PxSpacingInput } from './puckPxSpacingField';
import { PuckColorFieldWithPalette } from './createPuckColorField';
import { columnStyleOf, withColumnStyle } from '../../blocks/proposal/ProposalSection/sectionColumnStyles';

const COLUMNS_JUSTIFY_OPTIONS = [
  { value: 'flex-start', label: 'À gauche' },
  { value: 'center', label: 'Au centre' },
  { value: 'flex-end', label: 'À droite' },
  { value: 'space-between', label: 'Réparties' },
];

/** Fond et marge intérieure d'une colonne (panneau de couleur). */
function ColumnAppearance({ style, onChange }) {
  return (
    <Group gap="xs" grow align="flex-end">
      <PuckColorFieldWithPalette
        label="Fond"
        value={style.backgroundColor || ''}
        onChange={(backgroundColor) => onChange({ backgroundColor })}
        allowTransparent
      />
      <PxSpacingInput
        label="Marge intérieure"
        placeholder="0 px"
        value={style.padding || ''}
        onChange={(padding) => onChange({ padding })}
      />
    </Group>
  );
}

/** Largeur prise sur celle de la box (bouton, badge…) plutôt que sur la grille. */
function FitSwitch({ style, onChange }) {
  return (
    <Switch
      size="xs"
      label="Ajustée au contenu"
      checked={!!style.fit}
      onChange={(event) => onChange({ fit: event.currentTarget.checked })}
    />
  );
}

const usePuckApi = createUsePuck();

/**
 * Point d'empilement des colonnes — radios alignés sur les viewports Puck
 * (Small 360 / Medium 768), « Jamais » (0) ou largeur personnalisée en px.
 * Le champ px n'est actif que sur « Personnalisé » (défaut : valeur Small).
 */
function StackBreakpointControl({ stackAtPx, onChange }) {
  const choice = stackAtPx === STACK_AT_SMALL_PX
    ? 'small'
    : stackAtPx === STACK_AT_MEDIUM_PX
      ? 'medium'
      : stackAtPx === STACK_NEVER_PX
        ? 'never'
        : 'custom';

  return (
    <Radio.Group
      size="xs"
      value={choice}
      onChange={(v) => {
        if (v === 'small') onChange(STACK_AT_SMALL_PX);
        else if (v === 'medium') onChange(STACK_AT_MEDIUM_PX);
        else if (v === 'never') onChange(STACK_NEVER_PX);
        else onChange(STACK_AT_SMALL_PX + 1);
      }}
    >
      <Stack gap={6}>
        <Radio value="small" label={`Écrans étroits (≤ ${STACK_AT_SMALL_PX} px)`} />
        <Radio value="medium" label={`Mobile et tablette (≤ ${STACK_AT_MEDIUM_PX} px)`} />
        <Radio value="never" label="Jamais" />
        <Group gap={8} wrap="nowrap" align="center">
          <Radio value="custom" label="Personnalisé" />
          <NumberInput
            size="xs"
            w={110}
            disabled={choice !== 'custom'}
            value={choice === 'custom' ? stackAtPx : ''}
            onChange={(v) => {
              if (v === '' || v == null) return;
              onChange(Math.max(1, Math.round(Number(v))));
            }}
            min={1}
            max={4000}
            step={1}
            suffix=" px"
            clampBehavior="blur"
            allowDecimal={false}
          />
        </Group>
      </Stack>
    </Radio.Group>
  );
}

/**
 * Champ Puck — colonnes ProposalSection (ligne ou grille imbriquée).
 * `part` : la galerie de dispositions (« Mise en page »), ou le réglage fin des
 * colonnes, des espacements et de l'empilement (« Réglages avancés »). Deux
 * champs, une seule logique : les deux écrivent la même disposition.
 */
export default function ProposalSectionColumnsField({ onChange, part = 'layout' }) {
  const isAdvancedOpen = useAdvancedSectionOpen();
  const dispatch = usePuckApi((s) => s.dispatch);
  const sectionId = usePuckApi((s) => s.selectedItem?.props?.id);
  const itemType = usePuckApi((s) => s.selectedItem?.type);
  const itemProps = usePuckApi((s) => s.selectedItem?.props ?? {});
  const getSelectorForId = usePuckApi((s) => s.getSelectorForId);
  // Contenu des zones : lu au clic (sauvetage/pré-remplissage). Le champ n'est
  // monté que quand la section est sélectionnée — pas de coût pendant la
  // frappe dans un bloc texte (autre sélection → champ démonté).
  const zones = usePuckApi((s) => s.appState.data.zones);

  const sectionColumns = resolveSectionColumns(itemProps);
  const {
    preset,
    nested,
    columnIds,
    columnSpans,
  } = sectionColumns;
  const isRow = preset === SECTION_PRESET_ROW;
  const isNested = preset === SECTION_PRESET_NESTED && nested;
  const spanValues = getColumnSpanValues(columnIds, columnSpans);
  const mainSpanValues = isNested ? getMainColumnSpanValues(nested) : [];
  const columnStyles = itemProps.columnStyles || null;
  const hasFitColumn = Object.values(columnStyles || {}).some((style) => style.fit);

  const applyColumns = useCallback((next, { removedColumnIds = [] } = {}) => {
    onChange(next);
    if (!sectionId || typeof dispatch !== 'function') return;
    removedColumnIds.forEach((colId) => {
      dispatch({
        type: 'unregisterZone',
        zone: zoneKeyForSectionColumn(sectionId, colId),
      });
    });
  }, [dispatch, onChange, sectionId]);

  const applyWithCleanup = useCallback((next) => {
    const prevIds = collectSectionZoneIds(itemProps.sectionColumns);
    const nextIds = collectSectionZoneIds(next);
    const removed = getRemovedColumnIds(prevIds, nextIds);
    applyColumns(next, { removedColumnIds: removed });
  }, [applyColumns, itemProps.sectionColumns]);

  const patchNested = useCallback((nextNested) => {
    applyWithCleanup({ preset: SECTION_PRESET_NESTED, nested: nextNested });
  }, [applyWithCleanup]);

  const zoneKey = useCallback(
    (colId) => zoneKeyForSectionColumn(sectionId, colId),
    [sectionId],
  );
  const zoneContent = useCallback(
    (colId) => zones?.[zoneKey(colId)] ?? [],
    [zones, zoneKey],
  );

  /**
   * Changement de disposition — jamais destructif : les blocs des zones
   * retirées sont déplacés vers la dernière zone conservée (avant que la
   * zone source ne disparaisse), puis la nouvelle disposition est appliquée
   * (zones retirées, désormais vides, désenregistrées).
   */
  /** Applique un nouveau layout avec sauvetage : les blocs des zones retirées
   *  sont déplacés vers la dernière zone conservée avant le retrait. */
  const applyLayoutWithRescue = (next) => {
    const prevIds = collectSectionZoneIds(itemProps.sectionColumns);
    const nextIds = collectSectionZoneIds(next);
    const removed = getRemovedColumnIds(prevIds, nextIds);
    const canDispatch = sectionId && typeof dispatch === 'function';

    if (canDispatch && removed.some((colId) => zoneContent(colId).length > 0)) {
      const keptIds = nextIds.filter((colId) => prevIds.includes(colId));
      const rescueTarget = keptIds[keptIds.length - 1] ?? nextIds[nextIds.length - 1];
      let destIndex = zoneContent(rescueTarget).length;
      removed.forEach((colId) => {
        const count = zoneContent(colId).length;
        for (let i = 0; i < count; i += 1) {
          dispatch({
            type: 'move',
            sourceZone: zoneKey(colId),
            sourceIndex: 0,
            destinationZone: zoneKey(rescueTarget),
            destinationIndex: destIndex,
          });
          destIndex += 1;
        }
      });
    }

    applyColumns(next, { removedColumnIds: removed });
  };

  /** Écrit d'autres props du bloc que sectionColumns (le onChange du champ ne
   *  couvre que sa prop) : replace synchrone — createOnChange relit l'état
   *  frais, donc compose avec un onChange qui suit. */
  const updateItemProps = (patch) => {
    if (!sectionId || typeof dispatch !== 'function' || !itemType) return;
    const selector = getSelectorForId?.(sectionId);
    if (!selector) return;
    dispatch({
      type: 'replace',
      destinationZone: selector.zone,
      destinationIndex: selector.index,
      data: {
        type: itemType,
        props: { ...itemProps, ...patch },
      },
    });
  };

  const styleOf = (colId) => columnStyleOf(columnStyles, colId);
  const patchColumnStyle = (colId) => (patch) => updateItemProps({
    columnStyles: withColumnStyle(columnStyles, colId, patch),
  });

  /** Mémorise la config personnalisée courante (tuile « Personnalisé »). */
  const rememberCustomLayout = () => {
    updateItemProps({ savedCustomLayout: itemProps.sectionColumns });
  };

  const handleLayoutSelect = (def) => {
    const prevIds = collectSectionZoneIds(itemProps.sectionColumns);

    // Quitter un état personnalisé via un preset : snapshot pour pouvoir
    // revenir à cette config depuis la tuile « Personnalisé ».
    if (isSectionLayoutCustom(sectionColumns)) {
      rememberCustomLayout();
    }

    // Passage vers la disposition miroir (large+étroite ↔ étroite+large,
    // grand bloc+pile ↔ pile+grand bloc) : le contenu suit son rôle — le
    // grand bloc reste dans le grand bloc, la pile reste la pile.
    const currentDef = SECTION_LAYOUT_PRESETS.find(
      (d) => sectionMatchesLayoutPreset(sectionColumns, d),
    );
    const isMirrorSwitch = currentDef && SECTION_LAYOUT_MIRRORS[currentDef.key] === def.key;
    const next = buildSectionLayout(def, itemProps.sectionColumns, {
      reuseIds: isMirrorSwitch ? reorderIdsForMirror(currentDef, prevIds) : undefined,
    });
    applyLayoutWithRescue(next);
  };

  /** Tuile « Personnalisé » : restaure la config mémorisée (même mécanique de
   *  sauvetage que les presets — jamais destructif). */
  const handleRestoreCustom = () => {
    const saved = itemProps.savedCustomLayout;
    if (!saved) return;
    applyLayoutWithRescue(saved);
  };

  const handleAdd = () => {
    applyColumns(addSectionColumn(itemProps.sectionColumns));
  };

  const handleRemove = (index) => {
    const result = removeSectionColumn(itemProps.sectionColumns, index);
    // Jamais destructif : les blocs de la colonne supprimée rejoignent la
    // colonne voisine avant le retrait de la zone.
    if (result.removedColumnId && sectionId && typeof dispatch === 'function') {
      const neighbor = result.columnIds[Math.max(0, Math.min(index - 1, result.columnIds.length - 1))];
      const count = zoneContent(result.removedColumnId).length;
      if (neighbor != null) {
        let destIndex = zoneContent(neighbor).length;
        for (let i = 0; i < count; i += 1) {
          dispatch({
            type: 'move',
            sourceZone: zoneKey(result.removedColumnId),
            sourceIndex: 0,
            destinationZone: zoneKey(neighbor),
            destinationIndex: destIndex,
          });
          destIndex += 1;
        }
      }
    }
    applyColumns(
      {
        preset: SECTION_PRESET_ROW,
        columnIds: result.columnIds,
        columnSpans: result.columnSpans,
      },
      { removedColumnIds: result.removedColumnId ? [result.removedColumnId] : [] },
    );
  };

  const handleSpanChange = (index, raw) => {
    if (raw === '' || raw == null) return;
    const next = setSectionColumnSpan(itemProps.sectionColumns, index, raw);
    applyColumns({
      preset: SECTION_PRESET_ROW,
      columnIds: next.columnIds,
      columnSpans: next.columnSpans,
    });
  };

  /** « Lignes » sur une section en modèle ligne : opération nested-only →
   *  conversion à la volée vers le nested équivalent (mêmes ids de zones,
   *  contenu conservé), puis application du nombre de lignes. */
  const handleRowModelRowCount = (index, raw) => {
    if (raw === '' || raw == null) return;
    const n = Math.round(Number(raw));
    if (!Number.isFinite(n) || n <= 1) return;
    const converted = rowToNestedLayout(columnIds, columnSpans);
    patchNested(setNestedRowCount(converted, index, n));
  };

  if (part === 'layout') {
    return (
      <InspectorRow label="Disposition" layout="stacked">
        <SectionLayoutPresetPicker
          resolved={sectionColumns}
          onSelect={handleLayoutSelect}
          hasSavedCustom={!!itemProps.savedCustomLayout}
          onRestoreCustom={handleRestoreCustom}
        />
      </InspectorRow>
    );
  }

  if (!isAdvancedOpen) return null;

  return (
    <Stack gap={12}>
      {isNested && (
<NestedLayoutEditor
  nested={nested}
  mainSpanValues={mainSpanValues}
  styleOf={styleOf}
  patchColumnStyle={patchColumnStyle}
  onMainColumnCount={(n) => {
  if (n === '' || n == null) return;
  patchNested(applyNestedMainColumnCount(nested, n));
}}
  onMainSpan={(index, v) => {
    if (v === '' || v == null) return;
    patchNested(setNestedMainColumnSpan(nested, index, v));
  }}
  onRowCount={(mainIndex, n) => {
    if (n === '' || n == null) return;
    patchNested(setNestedRowCount(nested, mainIndex, n));
  }}
  onRowColumnCount={(mainIndex, rowIndex, n) => {
    if (n === '' || n == null) return;
    patchNested(setNestedRowColumnCount(nested, mainIndex, rowIndex, n));
  }}
  onRowColumnSpan={(mainIndex, rowIndex, colIndex, v) => {
    if (v === '' || v == null) return;
    patchNested(setNestedRowColumnSpan(nested, mainIndex, rowIndex, colIndex, v));
  }}
/>
      )}

      {isRow && (
        <>
  <Text size="xs" c="dimmed">
    Grille {GRID_COLUMNS} col. — ajustez chaque colonne ci-dessous.
  </Text>

  {columnIds.map((colId, index) => {
    const isLast = index === columnIds.length - 1;
    const label = `Colonne ${index + 1}`;

    return (
      <Stack key={colId} gap={4}>
        <Divider
          label={(
            <Group gap={4} wrap="nowrap">
              <Text size="xs" fw={500}>{label}</Text>
              <ActionIcon
                variant="subtle"
                color="red"
                size="xs"
                aria-label={`Supprimer ${label}`}
                disabled={columnIds.length <= 1}
                onClick={() => handleRemove(index)}
              >
                <FontAwesomeIcon
                  icon={icon({ name: 'trash', style: 'light' })}
                  style={{ fontSize: 14 }}
                />
              </ActionIcon>
            </Group>
          )}
          labelPosition="left"
        />
        <Group gap="xs" grow align="flex-start">
          {styleOf(colId).fit ? (
            <NumberInput size="xs" label="Largeur" value="" placeholder="Contenu" disabled readOnly />
          ) : isLast ? (
            <NumberInput
              size="xs"
              label="Largeur"
              value={spanValues[index]}
              suffix={`/ ${GRID_COLUMNS}`}
              disabled
              readOnly
              description="Complète la ligne"
            />
          ) : (
            <NumberInput
              size="xs"
              label="Largeur"
              value={getSpanAtMd(columnSpans[index])}
              onChange={(v) => handleSpanChange(index, v)}
              min={1}
              max={GRID_COLUMNS - 1}
              step={1}
              suffix={`/ ${GRID_COLUMNS}`}
              clampBehavior="strict"
            />
          )}
          <NumberInput
            size="xs"
            label="Lignes"
            value={1}
            onChange={(v) => handleRowModelRowCount(index, v)}
            min={1}
            max={8}
            step={1}
            clampBehavior="strict"
          />
        </Group>
        <FitSwitch style={styleOf(colId)} onChange={patchColumnStyle(colId)} />
        <ColumnAppearance style={styleOf(colId)} onChange={patchColumnStyle(colId)} />
      </Stack>
    );
  })}

  <Button
    size="xs"
    variant="light"
    leftSection={<FontAwesomeIcon icon={icon({ name: 'plus', style: 'light' })} style={{ fontSize: 14 }} />}
    onClick={handleAdd}
  >
    Ajouter une colonne
  </Button>
        </>
      )}

      <Divider label="Espacements" labelPosition="left" />
      {/* ?? '1rem' : les Blocs d'avant le champ n'ont pas la prop —
          ils rendent 16 px (resolveSectionGap), le champ l'affiche. */}
      <PxSpacingInput
        label="Espacement entre colonnes"
        placeholder="0 px"
        description="Vide = aucun espacement"
        value={itemProps.columnGap ?? '1rem'}
        onChange={(v) => updateItemProps({ columnGap: v })}
      />
      <PxSpacingInput
        label="Espacement entre lignes"
        placeholder="0 px"
        description="Vide = aucun espacement"
        value={itemProps.rowGap ?? '1rem'}
        onChange={(v) => updateItemProps({ rowGap: v })}
      />

      {hasFitColumn && (
        <Select
          size="xs"
          label="Placement des colonnes ajustées"
          data={COLUMNS_JUSTIFY_OPTIONS}
          value={itemProps.columnsJustify || 'flex-start'}
          onChange={(columnsJustify) => updateItemProps({ columnsJustify })}
          allowDeselect={false}
        />
      )}

      <Divider label="Empilement des colonnes" labelPosition="left" />
      <StackBreakpointControl
        stackAtPx={resolveStackAtPx(itemProps.stackAtPx)}
        onChange={(px) => updateItemProps({ stackAtPx: px })}
      />
    </Stack>
  );
}

function NestedLayoutEditor({
  nested,
  mainSpanValues,
  styleOf,
  patchColumnStyle,
  onMainColumnCount,
  onMainSpan,
  onRowCount,
  onRowColumnCount,
  onRowColumnSpan,
}) {
  const mainCount = nested.mainColumns.length;

  return (
    <Stack gap="sm">
      <NumberInput
        size="xs"
        label="Colonnes principales"
        value={mainCount}
        onChange={onMainColumnCount}
        min={1}
        max={4}
        step={1}
        clampBehavior="strict"
      />

      {nested.mainColumns.map((mainCol, mainIdx) => {
        const isLastMain = mainIdx === mainCount - 1;
        const mainLabel = `Colonne principale ${mainIdx + 1}`;

        return (
          <Stack key={mainCol.id} gap="xs">
            <Divider label={mainLabel} labelPosition="left" />
            {mainCount > 1 && (
              isLastMain ? (
                <NumberInput
                  size="xs"
                  label="Largeur"
                  value={getSpanAtMd(mainSpanValues[mainIdx])}
                  suffix={`/ ${GRID_COLUMNS}`}
                  disabled
                  readOnly
                  description="Complète la ligne"
                />
              ) : (
                <NumberInput
                  size="xs"
                  label="Largeur"
                  value={getSpanAtMd(nested.mainColumnSpans[mainIdx])}
                  onChange={(v) => onMainSpan(mainIdx, v)}
                  min={1}
                  max={GRID_COLUMNS - 1}
                  step={1}
                  suffix={`/ ${GRID_COLUMNS}`}
                  clampBehavior="strict"
                />
              )
            )}

            <NumberInput
              size="xs"
              label="Lignes"
              value={(mainCol.rows || []).length}
              onChange={(v) => onRowCount(mainIdx, v)}
              min={1}
              max={8}
              step={1}
              clampBehavior="strict"
            />
            <ColumnAppearance style={styleOf(mainCol.id)} onChange={patchColumnStyle(mainCol.id)} />

            {(mainCol.rows || []).map((row, rowIdx) => {
              const colCount = (row.columns || []).length;
              const rowSpanValues = getRowColumnSpanValues(row);

              return (
                <Stack key={row.id} gap="xs" pl="xs" style={{ borderLeft: '2px solid var(--mantine-color-gray-3)' }}>
                  <Text size="xs" fw={500}>Ligne {rowIdx + 1}</Text>
                  <NumberInput
                    size="xs"
                    label="Colonnes"
                    value={colCount}
                    onChange={(v) => onRowColumnCount(mainIdx, rowIdx, v)}
                    min={1}
                    max={6}
                    step={1}
                    clampBehavior="strict"
                  />
                  {colCount > 1 && (row.columns || []).map((cell, colIdx) => {
                    const isLastCol = colIdx === colCount - 1;
                    const fit = !!styleOf(cell.id).fit;
                    return (
                      <Stack key={cell.id} gap={4}>
                        <NumberInput
                          size="xs"
                          label={isLastCol ? `Col. ${colIdx + 1} (reste)` : `Col. ${colIdx + 1}`}
                          value={getSpanAtMd(rowSpanValues[colIdx])}
                          onChange={isLastCol ? undefined : (v) => onRowColumnSpan(mainIdx, rowIdx, colIdx, v)}
                          min={1}
                          max={GRID_COLUMNS - 1}
                          step={1}
                          suffix={`/ ${GRID_COLUMNS}`}
                          clampBehavior="strict"
                          disabled={isLastCol || fit}
                          readOnly={isLastCol || fit}
                        />
                        <FitSwitch style={styleOf(cell.id)} onChange={patchColumnStyle(cell.id)} />
                      </Stack>
                    );
                  })}
                </Stack>
              );
            })}
          </Stack>
        );
      })}
    </Stack>
  );
}

function getSpanAtMd(span) {
  if (typeof span === 'number') return span;
  if (span?.md != null) return span.md;
  if (span?.base != null) return span.base;
  return 6;
}
